import React, { useState } from 'react';
import { Plus, Search, ShieldCheck, Edit2, Copy, Trash2, MoreVertical, Power, PowerOff, AlertCircle } from 'lucide-react';
import {
  useValidationRules,
  useDeleteValidationRule,
  useCloneValidationRule,
  useUpdateValidationRule,
  type ValidationRule
} from '../../../services/queries/validationRuleQueries';
import { USE_GOBODO_API } from '../../../utils/gobodoUtils';
import { DevModeBanner } from '../../common/DevModeBanner';
import { ValidationRuleBuilder } from './ValidationRuleBuilder';
import { useToast } from '../../../contexts/ToastContext';
import {
  StandardButton,
  StandardSearchBar,
  StandardSelect,
  StandardLoadingState,
  StandardErrorState,
  StandardEmptyState
} from '../../standard';

interface ValidationRulesHomeProps {
  objectName: string;
  objectLabel: string;
  allFields: any[];
}

export const ValidationRulesHome: React.FC<ValidationRulesHomeProps> = ({
  objectName,
  objectLabel,
  allFields
}) => {
  const { success, error: showError } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [ruleBuilderOpen, setRuleBuilderOpen] = useState<{ rule?: ValidationRule; objectName: string } | null>(null);
  const [ruleMenuOpen, setRuleMenuOpen] = useState<string | null>(null);

  const { data: rules, isLoading, error } = useValidationRules(objectName);
  const deleteRuleMutation = useDeleteValidationRule();
  const cloneRuleMutation = useCloneValidationRule();
  const updateRuleMutation = useUpdateValidationRule();

  const filteredRules = rules?.filter(rule => {
    const matchesSearch = rule.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         rule.description?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter === 'all' ||
                         (statusFilter === 'active' && rule.isActive) ||
                         (statusFilter === 'inactive' && !rule.isActive);

    return matchesSearch && matchesStatus;
  });

  const totalRules = rules?.length || 0;
  const activeRules = rules?.filter(r => r.isActive).length || 0;
  const inactiveRules = totalRules - activeRules;

  const handleToggleActive = async (ruleId: string, currentStatus: boolean) => {
    try {
      await updateRuleMutation.mutateAsync({
        ruleId,
        updates: { isActive: !currentStatus }
      });
      setRuleMenuOpen(null);
    } catch (error: any) {
      showError('Failed to update rule status', error.message || 'Please try again');
    }
  };

  const handleDelete = async (ruleId: string, ruleName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${ruleName}"?`)) return;

    try {
      await deleteRuleMutation.mutateAsync({ ruleId, objectName });
      success('Validation rule deleted successfully!', `${ruleName} has been permanently removed`);
      setRuleMenuOpen(null);
    } catch (error: any) {
      showError('Failed to delete validation rule', error.message || 'Please try again');
    }
  };

  const handleClone = async (ruleId: string) => {
    try {
      const cloned = await cloneRuleMutation.mutateAsync(ruleId);
      setRuleBuilderOpen({ rule: cloned, objectName });
      setRuleMenuOpen(null);
    } catch (error) {
      console.error('Failed to clone validation rule:', error);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Validation Rules</h2>
            <p className="text-gray-600 dark:text-gray-300 mt-1">
              Define data quality rules for {objectLabel} records
            </p>
          </div>
          <StandardButton
            variant="primary"
            leftIcon={<Plus className="w-4 h-4" />}
            onClick={() => setRuleBuilderOpen({ objectName })}
          >
            New Validation Rule
          </StandardButton>
        </div>

        {/* Development Mode Banner */}
        <DevModeBanner
          dataSource={USE_GOBODO_API ? 'Gobodo API' : 'mockDataManager.ts'}
          itemCount={rules ? `${rules.length} validation rules` : 'validation rules'}
          customMessage={USE_GOBODO_API
            ? `Gobodo: ${rules ? `Showing ${rules.length} validation rules` : 'Loading'} from Gobodo API. Changes persist to backend.`
            : `Development Mode: ${rules ? `Showing ${rules.length} validation rules` : 'Using mock data'} (in-memory).`}
          dismissible={true}
        />

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {totalRules}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Total Rules</div>
              </div>
              <div className="p-3 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                <ShieldCheck className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {activeRules}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Active Rules</div>
              </div>
              <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-lg">
                <Power className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {inactiveRules}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Inactive Rules</div>
              </div>
              <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
                <PowerOff className="w-6 h-6 text-gray-600 dark:text-gray-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1">
            <StandardSearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search validation rules..."
            />
          </div>

          <StandardSelect
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
            options={[
              { value: 'all', label: 'All Status' },
              { value: 'active', label: 'Active Only' },
              { value: 'inactive', label: 'Inactive Only' }
            ]}
          />
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <StandardLoadingState message="Loading validation rules..." />
      )}

      {/* Error State */}
      {error && (
        <StandardErrorState
          title="Error loading validation rules"
          message="Please try again."
        />
      )}

      {/* Rules List */}
      {!isLoading && !error && (
        <>
          {filteredRules && filteredRules.length > 0 ? (
            <div className="space-y-3">
              {filteredRules.map((rule) => (
                <RuleCard
                  key={rule.id}
                  rule={rule}
                  onEdit={() => setRuleBuilderOpen({ rule, objectName })}
                  onClone={() => handleClone(rule.id)}
                  onDelete={() => handleDelete(rule.id, rule.name)}
                  onToggleActive={() => handleToggleActive(rule.id, rule.isActive)}
                  menuOpen={ruleMenuOpen === rule.id}
                  onMenuToggle={() => setRuleMenuOpen(ruleMenuOpen === rule.id ? null : rule.id)}
                />
              ))}
            </div>
          ) : (
            <StandardEmptyState
              icon={<ShieldCheck className="w-16 h-16" />}
              title={searchQuery || statusFilter !== 'all' ? 'No validation rules found' : 'No validation rules yet'}
              message={searchQuery || statusFilter !== 'all'
                ? 'Try adjusting your search or filter criteria'
                : 'Create your first validation rule to enforce data quality'
              }
              actionLabel={searchQuery || statusFilter !== 'all' ? 'Clear filters' : 'Create Validation Rule'}
              onAction={searchQuery || statusFilter !== 'all'
                ? () => { setSearchQuery(''); setStatusFilter('all'); }
                : () => setRuleBuilderOpen({ objectName })
              }
            />
          )}
        </>
      )}

      {/* Validation Rule Builder Modal */}
      {ruleBuilderOpen && (
        <ValidationRuleBuilder
          rule={ruleBuilderOpen.rule}
          objectName={ruleBuilderOpen.objectName}
          objectLabel={objectLabel}
          allFields={allFields}
          onClose={() => setRuleBuilderOpen(null)}
        />
      )}
    </div>
  );
};

// Rule Card Component
interface RuleCardProps {
  rule: ValidationRule;
  onEdit: () => void;
  onClone: () => void;
  onDelete: () => void;
  onToggleActive: () => void;
  menuOpen: boolean;
  onMenuToggle: () => void;
}

const RuleCard: React.FC<RuleCardProps> = ({
  rule,
  onEdit,
  onClone,
  onDelete,
  onToggleActive,
  menuOpen,
  onMenuToggle
}) => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div className={`p-3 rounded-lg flex-shrink-0 ${
          rule.isActive
            ? 'bg-green-100 dark:bg-green-900/20'
            : 'bg-gray-100 dark:bg-gray-700'
        }`}>
          <ShieldCheck className={`w-6 h-6 ${
            rule.isActive
              ? 'text-green-600 dark:text-green-400'
              : 'text-gray-600 dark:text-gray-400'
          }`} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                  {rule.name}
                </h3>
                {rule.isActive ? (
                  <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 text-xs rounded font-medium flex items-center gap-1">
                    <Power className="w-3 h-3" />
                    Active
                  </span>
                ) : (
                  <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400 text-xs rounded font-medium flex items-center gap-1">
                    <PowerOff className="w-3 h-3" />
                    Inactive
                  </span>
                )}
              </div>
              {rule.description && (
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                  {rule.description}
                </p>
              )}
            </div>

            <div className="relative ml-4">
              <button
                onClick={onMenuToggle}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              >
                <MoreVertical className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              </button>

              {menuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={onMenuToggle}
                  />
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-20">
                    <div className="py-1">
                      <button
                        onClick={onEdit}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                      >
                        <Edit2 className="w-4 h-4" />
                        Edit Rule
                      </button>
                      <button
                        onClick={onToggleActive}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                      >
                        {rule.isActive ? (
                          <>
                            <PowerOff className="w-4 h-4" />
                            Deactivate
                          </>
                        ) : (
                          <>
                            <Power className="w-4 h-4" />
                            Activate
                          </>
                        )}
                      </button>
                      <button
                        onClick={onClone}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                      >
                        <Copy className="w-4 h-4" />
                        Clone Rule
                      </button>
                      <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                      <button
                        onClick={onDelete}
                        className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete Rule
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Error Message Preview */}
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded px-3 py-2 mb-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800 dark:text-red-300">
                {rule.errorMessage}
              </p>
            </div>
          </div>

          {/* Metadata */}
          <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
            <div>{rule.conditions.length} condition{rule.conditions.length !== 1 ? 's' : ''}</div>
            <span>•</span>
            <div>{rule.logicOperator} logic</div>
            <span>•</span>
            <div>Error on {rule.errorLocation === 'top' ? 'page top' : 'field'}</div>
          </div>

          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Modified {new Date(rule.lastModified).toLocaleDateString()} by {rule.lastModifiedBy}
          </div>
        </div>
      </div>
    </div>
  );
};

